<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const alert = useAlert;

const uiFlags = computed(() => store.getters['bulkCampaigns/getUIFlags']);
const campaign = computed(() => store.getters['bulkCampaigns/getCurrent']);
const isLoading = computed(() => uiFlags.value.isLoadingRecord);

const filterContact = ref('');
const currentPage = ref(1);
const pageSize = 20;

const consolidatedStatus = contact => {
  if (
    contact.status === 'no_whatsapp' ||
    contact.delivery_status === 'no_whatsapp'
  ) {
    return {
      label: 'Sem WhatsApp',
      color:
        'bg-slate-200 text-slate-700 dark:bg-slate-700/40 dark:text-slate-300',
    };
  }
  if (
    contact.status === 'failed' ||
    contact.delivery_status === 'delivery_failed'
  ) {
    return {
      label: 'Falha no envio',
      color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    };
  }
  if (contact.status === 'skipped') {
    return {
      label: 'Cancelado',
      color: 'bg-n-gray-3 text-n-gray-11',
    };
  }
  if (contact.status === 'pending') {
    return {
      label: 'Aguardando fila',
      color: 'bg-n-amber-3 text-n-amber-11',
    };
  }
  if (contact.status === 'sending') {
    return {
      label: 'Enviando',
      color: 'bg-n-blue-3 text-n-blue-11',
    };
  }

  const map = {
    unconfirmed: {
      label: 'Aguardando confirmação',
      color: 'bg-n-iris-3 text-n-iris-11',
    },
    accepted: {
      label: 'Enviada',
      color: 'bg-n-teal-3 text-n-teal-11',
    },
    delivered: {
      label: 'Entregue',
      color:
        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    },
    read: {
      label: 'Lida',
      color: 'bg-green-600 text-white dark:bg-green-500 dark:text-white',
    },
  };
  return map[contact.delivery_status] || map.unconfirmed;
};

// eslint-disable-next-line no-unused-vars
const campaignStatusColor = status => {
  const map = {
    scheduled:
      'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    processing:
      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    completed:
      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    paused:
      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  };
  return map[status] || map.draft;
};

const campaignStatusLabel = status => {
  const map = {
    scheduled: t('BULK_CAMPAIGNS.STATUS.SCHEDULED'),
    processing: t('BULK_CAMPAIGNS.STATUS.PROCESSING'),
    completed: t('BULK_CAMPAIGNS.STATUS.COMPLETED'),
    cancelled: t('BULK_CAMPAIGNS.STATUS.CANCELLED'),
    failed: t('BULK_CAMPAIGNS.STATUS.FAILED'),
    paused: t('BULK_CAMPAIGNS.STATUS.PAUSED'),
    draft: t('BULK_CAMPAIGNS.STATUS.DRAFT'),
  };
  return map[status] || status;
};

const formatDate = dateStr => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const contacts = computed(() => {
  if (!campaign.value?.contacts || !Array.isArray(campaign.value.contacts))
    return [];
  let list = campaign.value.contacts;
  if (filterContact.value) {
    const q = filterContact.value.toLowerCase();
    list = list.filter(
      c =>
        c.phone_number?.toLowerCase().includes(q) ||
        c.contact_name?.toLowerCase().includes(q)
    );
  }
  return list;
});

const paginatedContacts = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return contacts.value.slice(start, start + pageSize);
});

const totalPages = computed(() => Math.ceil(contacts.value.length / pageSize));

const canCancel = computed(() =>
  ['scheduled', 'processing', 'paused', 'draft'].includes(
    campaign.value?.status
  )
);
const canPause = computed(() => campaign.value?.status === 'processing');
const canResume = computed(() => campaign.value?.status === 'paused');
const canDelete = computed(() =>
  ['completed', 'cancelled', 'failed', 'draft'].includes(campaign.value?.status)
);
const canSendNow = computed(() =>
  ['draft', 'scheduled'].includes(campaign.value?.status)
);

const fetchCampaign = () =>
  store.dispatch('bulkCampaigns/get', { id: route.params.id });

const handleCancel = async () => {
  if (!window.confirm(t('BULK_CAMPAIGNS.CONFIRM_CANCEL'))) return;
  try {
    await store.dispatch('bulkCampaigns/cancel', { id: route.params.id });
    await fetchCampaign();
    alert(t('BULK_CAMPAIGNS.CANCELLED_SUCCESS'));
  } catch (e) {
    alert(t('BULK_CAMPAIGNS.ERRORS.ACTION_FAILED'));
  }
};

const handlePause = async () => {
  try {
    await store.dispatch('bulkCampaigns/pause', { id: route.params.id });
    await fetchCampaign();
    alert(t('BULK_CAMPAIGNS.PAUSED_SUCCESS'));
  } catch (e) {
    alert(t('BULK_CAMPAIGNS.ERRORS.ACTION_FAILED'));
  }
};

const handleResume = async () => {
  try {
    await store.dispatch('bulkCampaigns/resume', { id: route.params.id });
    await fetchCampaign();
    alert(t('BULK_CAMPAIGNS.RESUMED_SUCCESS'));
  } catch (e) {
    alert(t('BULK_CAMPAIGNS.ERRORS.ACTION_FAILED'));
  }
};

const handleDelete = async () => {
  if (!window.confirm(t('BULK_CAMPAIGNS.CONFIRM_DELETE'))) return;
  try {
    await store.dispatch('bulkCampaigns/delete', { id: route.params.id });
    alert(t('BULK_CAMPAIGNS.DELETED_SUCCESS'));
    router.push({ name: 'bulk_campaigns_main' });
  } catch (e) {
    alert(t('BULK_CAMPAIGNS.ERRORS.DELETE_FAILED'));
  }
};

const handleSendNow = async () => {
  try {
    await store.dispatch('bulkCampaigns/sendNow', { id: route.params.id });
    await fetchCampaign();
    alert(t('BULK_CAMPAIGNS.SENT_NOW_SUCCESS'));
  } catch (e) {
    alert(t('BULK_CAMPAIGNS.ERRORS.ACTION_FAILED'));
  }
};

onMounted(() => {
  fetchCampaign();
});

onUnmounted(() => {
  store.dispatch('bulkCampaigns/clearCurrent');
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header
      class="sticky top-0 z-10 px-6 bg-n-surface-1 border-b border-n-divider"
    >
      <div
        class="w-full max-w-7xl mx-auto h-20 flex items-center justify-between gap-4"
      >
        <button
          class="p-2 rounded-lg text-n-text-light hover:bg-n-surface-2 transition-colors"
          :title="t('COMMON.BACK')"
          @click="router.back()"
        >
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <div class="flex-1">
          <div class="flex items-center gap-2">
            <h1 class="text-heading-1 text-n-slate-12 truncate">
              {{ campaign?.name || t('BULK_CAMPAIGNS.TITLE') }}
            </h1>
            <span
              class="text-sm font-mono text-n-text-light dark:text-n-text-muted bg-n-surface-3 px-2 py-0.5 rounded"
              >{{ campaign?.id }}</span
            >
          </div>
          <p class="text-sm text-n-text-light dark:text-n-text-muted mt-1">
            {{ t('BULK_CAMPAIGNS.DETAILS.TITLE') }}
          </p>
        </div>
        <div class="flex items-center gap-3">
          <span
            v-if="campaign"
            class="px-3 py-1 text-sm font-medium rounded-full"
            :class="[campaignStatusColor(campaign.status)]"
            >{{ campaignStatusLabel(campaign.status) }}</span
          >
        </div>
      </div>
    </header>

    <main v-if="campaign" class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-7xl mx-auto py-6 space-y-6">
        <div v-if="isLoading" class="flex justify-center items-center py-20">
          <Spinner />
        </div>
        <template v-else>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="md:col-span-2 space-y-4">
              <div
                class="bg-white dark:bg-n-surface-2 rounded-xl border border-n-divider p-6"
              >
                <h3 class="text-heading-3 text-n-slate-12 mb-4">
                  {{ t('BULK_CAMPAIGNS.DETAILS.PROGRESS') }}
                </h3>
                <div
                  class="w-full bg-n-surface-3 rounded-full h-3 overflow-hidden mb-2"
                >
                  <div
                    class="bg-n-primary-500 h-full rounded-full transition-all duration-500"
                    :style="{ width: campaign.progress_percentage + '%' }"
                  />
                </div>
                <div
                  class="flex justify-between text-sm text-n-text-light dark:text-n-text-muted"
                >
                  <span
                    >{{ campaign.contacts_sent || 0 }} /
                    {{ campaign.total_contacts || 0 }}
                    {{ t('BULK_CAMPAIGNS.DETAILS.CONTACTS_SENT') }}</span
                  >
                  <span>{{ campaign.progress_percentage }}%</span>
                </div>
              </div>

              <div
                class="bg-white dark:bg-n-surface-2 rounded-xl border border-n-divider p-6"
              >
                <h3 class="text-heading-3 text-n-slate-12 mb-4">
                  {{ t('BULK_CAMPAIGNS.DETAILS.TITLE') }}
                </h3>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span class="text-n-text-light dark:text-n-text-muted">{{
                      t('BULK_CAMPAIGNS.DETAILS.TOTAL_CONTACTS')
                    }}</span
                    ><br /><span class="text-n-slate-12 font-medium">{{
                      campaign.total_contacts || 0
                    }}</span>
                  </div>
                  <div>
                    <span class="text-n-text-light dark:text-n-text-muted">{{
                      t('BULK_CAMPAIGNS.DETAILS.CONTACTS_SENT')
                    }}</span
                    ><br /><span
                      class="text-n-slate-12 font-medium text-green-600"
                      >{{ campaign.contacts_sent || 0 }}</span
                    >
                  </div>
                  <div>
                    <span class="text-n-text-light dark:text-n-text-muted">{{
                      t('BULK_CAMPAIGNS.DETAILS.CONTACTS_FAILED')
                    }}</span
                    ><br /><span
                      class="text-n-slate-12 font-medium text-red-600"
                      >{{ campaign.contacts_failed || 0 }}</span
                    >
                  </div>
                  <div>
                    <span class="text-n-text-light dark:text-n-text-muted">{{
                      t('BULK_CAMPAIGNS.DETAILS.CURRENT_BATCH')
                    }}</span
                    ><br /><span class="text-n-slate-12 font-medium"
                      >{{ campaign.current_batch || 0 }} /
                      {{ campaign.total_batches || 0 }}</span
                    >
                  </div>
                </div>
              </div>

              <div
                class="bg-white dark:bg-n-surface-2 rounded-xl border border-n-divider p-6"
              >
                <h3 class="text-heading-3 text-n-slate-12 mb-4">
                  {{ t('BULK_CAMPAIGNS.BATCH_CONFIG') }}
                </h3>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span class="text-n-text-light dark:text-n-text-muted">{{
                      t('BULK_CAMPAIGNS.BATCH_SIZE')
                    }}</span
                    ><br /><span class="text-n-slate-12 font-medium">{{
                      campaign.batch_size || 10
                    }}</span>
                  </div>
                  <div>
                    <span class="text-n-text-light dark:text-n-text-muted">{{
                      t('BULK_CAMPAIGNS.BATCH_INTERVAL')
                    }}</span
                    ><br /><span class="text-n-slate-12 font-medium">{{
                      t('BULK_CAMPAIGNS.TIME.MINUTES_SHORT', {
                        count: campaign.batch_interval_minutes || 3,
                      })
                    }}</span>
                  </div>
                  <div>
                    <span class="text-n-text-light dark:text-n-text-muted">{{
                      t('BULK_CAMPAIGNS.DELAY_MIN')
                    }}</span
                    ><br /><span class="text-n-slate-12 font-medium">{{
                      t('BULK_CAMPAIGNS.TIME.SECONDS_SHORT', {
                        count: campaign.delay_between_messages_min || 5,
                      })
                    }}</span>
                  </div>
                  <div>
                    <span class="text-n-text-light dark:text-n-text-muted">{{
                      t('BULK_CAMPAIGNS.DELAY_MAX')
                    }}</span
                    ><br /><span class="text-n-slate-12 font-medium">{{
                      t('BULK_CAMPAIGNS.TIME.SECONDS_SHORT', {
                        count: campaign.delay_between_messages_max || 15,
                      })
                    }}</span>
                  </div>
                </div>
                <div
                  v-if="campaign.scheduled_at"
                  class="mt-4 text-sm text-n-text-light dark:text-n-text-muted"
                >
                  {{ t('BULK_CAMPAIGNS.DETAILS.NEXT_BATCH_AT') }}
                  {{ formatDate(campaign.scheduled_at) }}
                </div>
              </div>

              <div class="space-y-4">
                <div
                  class="bg-white dark:bg-n-surface-2 rounded-xl border border-n-divider p-4"
                >
                  <h4 class="font-medium text-n-slate-12 mb-3">
                    {{ t('BULK_CAMPAIGNS.ACTIONS.CANCEL') }}
                  </h4>
                  <div class="flex flex-col gap-2">
                    <button
                      v-if="canCancel"
                      class="w-full px-3 py-2 text-sm text-red-500 border border-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      @click="handleCancel"
                    >
                      {{ t('BULK_CAMPAIGNS.ACTIONS.CANCEL') }}
                    </button>
                    <button
                      v-if="canPause"
                      class="w-full px-3 py-2 text-sm text-orange-500 border border-orange-500 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                      @click="handlePause"
                    >
                      {{ t('BULK_CAMPAIGNS.ACTIONS.PAUSE') }}
                    </button>
                    <button
                      v-if="canResume"
                      class="w-full px-3 py-2 text-sm text-green-500 border border-green-500 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                      @click="handleResume"
                    >
                      {{ t('BULK_CAMPAIGNS.ACTIONS.RESUME') }}
                    </button>
                    <button
                      v-if="canSendNow"
                      class="w-full px-3 py-2 text-sm text-n-primary-600 border border-n-primary-600 rounded-lg hover:bg-n-primary-50 dark:hover:bg-n-primary-900/20 transition-colors"
                      @click="handleSendNow"
                    >
                      {{ t('BULK_CAMPAIGNS.ACTIONS.SEND_NOW') }}
                    </button>
                    <button
                      v-if="canDelete"
                      class="w-full px-3 py-2 text-sm text-red-500 border border-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      @click="handleDelete"
                    >
                      {{ t('BULK_CAMPAIGNS.ACTIONS.DELETE') }}
                    </button>
                  </div>
                </div>

                <div
                  class="bg-white dark:bg-n-surface-2 rounded-xl border border-n-divider p-4"
                >
                  <h4 class="font-medium text-n-slate-12 mb-3">
                    {{ t('BULK_CAMPAIGNS.MESSAGE') }}
                  </h4>
                  <pre
                    class="text-sm text-n-text-light dark:text-n-text-muted bg-n-surface-3 p-3 rounded-lg whitespace-pre-wrap max-h-48 overflow-auto"
                    >{{ campaign.content }}</pre
                  >
                </div>
              </div>
            </div>

            <div
              class="bg-white dark:bg-n-surface-2 rounded-xl border border-n-divider"
            >
              <div
                class="p-4 border-b border-n-divider flex items-center justify-between"
              >
                <h3 class="text-heading-3 text-n-slate-12">
                  {{ t('BULK_CAMPAIGNS.DETAILS.CONTACTS_LIST') }} ({{
                    contacts.length
                  }})
                </h3>
                <input
                  v-model="filterContact"
                  type="text"
                  :placeholder="t('BULK_CAMPAIGNS.DETAILS.SEARCH_CONTACTS')"
                  class="px-3 py-1.5 border border-n-divider rounded-lg bg-white dark:bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-primary-500 w-64"
                />
              </div>

              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead class="bg-n-surface-2">
                    <tr>
                      <th
                        class="px-4 py-3 text-left text-xs font-medium text-n-text-light dark:text-n-text-muted uppercase tracking-wider"
                      >
                        {{ t('BULK_CAMPAIGNS.DETAILS.COLUMNS.PHONE') }}
                      </th>
                      <th
                        class="px-4 py-3 text-left text-xs font-medium text-n-text-light dark:text-n-text-muted uppercase tracking-wider"
                      >
                        {{ t('BULK_CAMPAIGNS.DETAILS.COLUMNS.NAME') }}
                      </th>
                      <th
                        class="px-4 py-3 text-left text-xs font-medium text-n-text-light dark:text-n-text-muted uppercase tracking-wider"
                      >
                        {{ t('BULK_CAMPAIGNS.DETAILS.COLUMNS.STATUS') }}
                      </th>
                      <th
                        class="px-4 py-3 text-left text-xs font-medium text-n-text-light dark:text-n-text-muted uppercase tracking-wider"
                      >
                        {{ t('BULK_CAMPAIGNS.DETAILS.COLUMNS.BATCH') }}
                      </th>
                      <th
                        class="px-4 py-3 text-left text-xs font-medium text-n-text-light dark:text-n-text-muted uppercase tracking-wider"
                      >
                        {{ t('BULK_CAMPAIGNS.DETAILS.COLUMNS.SENT_AT') }}
                      </th>
                      <th
                        class="px-4 py-3 text-left text-xs font-medium text-n-text-light dark:text-n-text-muted uppercase tracking-wider"
                      >
                        {{ t('BULK_CAMPAIGNS.DETAILS.COLUMNS.ERROR') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-n-divider">
                    <tr
                      v-for="contact in paginatedContacts"
                      :key="contact.id"
                      class="hover:bg-n-surface-2"
                    >
                      <td class="px-4 py-3 text-sm font-mono text-n-slate-11">
                        {{ contact.formatted_phone || contact.phone_number }}
                      </td>
                      <td class="px-4 py-3 text-sm text-n-slate-11">
                        {{ contact.contact_name || '-' }}
                      </td>
                      <td class="px-4 py-3">
                        <span
                          class="px-2 py-0.5 text-xs rounded-full whitespace-nowrap"
                          :class="[consolidatedStatus(contact).color]"
                          >{{ consolidatedStatus(contact).label }}</span
                        >
                      </td>
                      <td
                        class="px-4 py-3 text-sm text-n-text-light dark:text-n-text-muted"
                      >
                        {{ contact.batch_number || '-' }}
                      </td>
                      <td
                        class="px-4 py-3 text-sm text-n-text-light dark:text-n-text-muted"
                      >
                        {{
                          contact.sent_at ? formatDate(contact.sent_at) : '-'
                        }}
                      </td>
                      <td
                        class="px-4 py-3 text-sm text-red-500 max-w-xs truncate"
                      >
                        {{ contact.error_message || '-' }}
                      </td>
                    </tr>
                    <tr v-if="paginatedContacts.length === 0">
                      <td
                        colspan="6"
                        class="px-4 py-8 text-center text-n-text-light dark:text-n-text-muted"
                      >
                        {{ t('BULK_CAMPAIGNS.EMPTY') }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                v-if="totalPages > 1"
                class="px-4 py-3 border-t border-n-divider flex items-center justify-between"
              >
                <span class="text-sm text-n-text-light dark:text-n-text-muted"
                  >{{ t('BULK_CAMPAIGNS.DETAILS.RESULTS') }}
                  {{ (currentPage - 1) * pageSize + 1 }}-{{
                    Math.min(currentPage * pageSize, contacts.length)
                  }}
                  {{ t('BULK_CAMPAIGNS.DETAILS.OF') }}
                  {{ contacts.length }}</span
                >
                <div class="flex gap-2">
                  <button
                    :disabled="currentPage === 1"
                    class="px-3 py-1.5 text-sm border border-n-divider rounded-lg disabled:opacity-50"
                    @click="currentPage--"
                  >
                    {{ t('COMMON.PREVIOUS') }}
                  </button>
                  <button
                    :disabled="currentPage === totalPages"
                    class="px-3 py-1.5 text-sm border border-n-divider rounded-lg disabled:opacity-50"
                    @click="currentPage++"
                  >
                    {{ t('COMMON.NEXT') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </main>
  </section>
</template>

<style scoped>
input[type='text'] {
  margin-bottom: 0 !important;
}
</style>
